// Pure, framework-free helpers for the company type catalog and company profile
// photos. The `Company` interface itself (and its non-derived fields) lives in
// `types.ts`; this module owns the closed set of company types and the mapping
// from the server-internal `StoredCompanyPhoto` to the client DTO — keeping both
// trivially testable and shared by the client and the server.

import type { Company, CompanyPhoto, CompanyType, StoredCompanyPhoto } from './types';

/**
 * Every company type offered at creation, in display order. Only Sport Club
 * exists for now; new types are added here (and to the `CompanyType` union and
 * the Postgres enum) as the platform grows.
 */
export const COMPANY_TYPES: readonly CompanyType[] = ['SPORT_CLUB'];

/** The type a company defaults to when none is supplied (also the only one). */
export const DEFAULT_COMPANY_TYPE: CompanyType = 'SPORT_CLUB';

/** Whether `value` is a supported company type code. */
export function isCompanyType(value: unknown): value is CompanyType {
  return (
    typeof value === 'string' && (COMPANY_TYPES as readonly string[]).includes(value)
  );
}

/** Narrow an arbitrary value to a supported `CompanyType`, or `null`. */
export function toCompanyType(value: unknown): CompanyType | null {
  return isCompanyType(value) ? value : null;
}

/**
 * Whether the company has passed the platform's review. Companies created by
 * platform staff are verified from the start; one that signed itself up through
 * the public wizard stays unverified — unable to add athletes, coaches or users
 * — until a Super Admin verifies it (see `features/company-registration.md`).
 */
export function isCompanyVerified(
  company: Pick<Company, 'verifiedAt'>,
): boolean {
  return company.verifiedAt !== null;
}

/** Project a stored company photo to the client DTO, dropping the storage key. */
export function toCompanyPhoto(photo: StoredCompanyPhoto): CompanyPhoto {
  return {
    id: photo.id,
    url: photo.webpUrl,
    width: photo.width,
    height: photo.height,
    orientation: photo.orientation,
    createdAt: photo.createdAt,
  };
}
